import { EndPoints, ItemTypes, UiVersion } from "./Constants";
import { Dispatch } from "@reduxjs/toolkit";
import { api } from "./AxiosConfig";
import {
  CategorizedProducts,
  Category,
  RegistrationRequest,
  ServerOrder,
  WaitlistRequest,
} from "../types/Generic";
import {
  addCart,
  manageModal,
  MODAL_TRIGGER,
  OrderSubmitState,
  setCategories,
  setCategorizedProducts,
  setCustomerOrders,
  setTableSessionBill,
  setOrderSubmitState,
} from "../redux/slices/Items";
import { SetCategorizedProducts } from "../types/PayloadActionTypes";
import {
  RegistrationSubmitState,
  setLoadingState,
  setMenuTheme,
  setRegistrationSubmitState,
  setUser,
  TableContext,
  setWaitlistSubmitState,
  User,
  WaitListSubmitState,
} from "../redux/slices/Client";
import { set } from "lockr";
import { Feedback } from "../FeedbackModal";

export const getProdBaseURL = () => {
  let url = null;
  const baseSite = window.location.origin;
  if (baseSite === process.env.REACT_APP_CLIENT_ORIGIN_TESTING) {
    url = process.env.REACT_APP_API_URL_PROD_TESTING;
  }
  if (baseSite === process.env.REACT_APP_CLIENT_ORIGIN_PROD) {
    url = process.env.REACT_APP_API_URL_PROD;
  }

  return url || "";
};

export const getClient = async (
  username: string,
  segment: string
): Promise<User> => {
  const userSettings = await api.get(`/user/${username}/${segment}/settings`);
  return userSettings.data;
};

export const resolveTable = async (
  username: string,
  segment: string,
  tableNumber: string
): Promise<TableContext | null> => {
  if (!username || !segment || !tableNumber) return null;

  const response = await api.get(
    EndPoints.TABLE.RESOLVE(username, segment, tableNumber)
  );

  return response.data.table || null;
};

// todo: This function should be deprecated after completing switch to
//  url segmenting.
export const fetchUserSettings = (
  tableNum: string,
  identificationNumber: number,
  id: string,
  dispatch: Dispatch<any>
): Promise<boolean> => {
  return api
    .get(`/user/${identificationNumber}/settings`)
    .then((response) => {
      if (response.data) {
        const user: User = {
          id: response.data.id,
          theme: UiVersion.DEMO,
          segment: "",
          username: "",
          currency: response.data.currency,
          languages: response.data.languages,
          hasSegments: response.data.hasSegments,
          defaultLang: response.data.defaultLang,
          name: response.data.name,
          address: response.data.address,
          brief: response.data.brief,
          businessProfile: response.data.businessProfile || null,
          wifi: response.data.wifi,
          contacts: response.data.contacts,
          isDemo: response.data.isDemo,
          activities: response.data.activities,
          canPlaceOrder: response.data.canPlaceOrder,
          clientVersion: response.data.clientVersion,
          banner: response.data.banner,
          logo: response.data.logo,
        };
        const slug = response.data.name.toLowerCase().replaceAll(" ", "-");
        dispatch(setUser({ user, tableNum }));
        set("user-slug", slug);
        return true;
      }
      return false;
    })
    .catch((error) => {
      console.log(error);
      dispatch(setCategories({ isSuccess: false }));
      return false;
    });
};

export const fetchItems = (
  clientId: string,
  dispatch: Dispatch<any>,
  activeProductType: number,
  segment: string
): Promise<boolean> => {
  const base = EndPoints.ALL.ITEMS;
  const path = `${base}/${clientId}/items`;

  return api
    .get(path)
    .then((response) => {
      const { drink_categories, food_categories, combos, menuTheme } =
        response.data;

      const foodCategory: Category[] = food_categories.map((category: any) => {
        return {
          id: category.id,
          name: category.name,
          product: category.food,
        };
      });
      const drinkCategory: Category[] = drink_categories.map(
        (category: any) => {
          return {
            id: category.id,
            name: category.name,
            product: category.drinks,
          };
        }
      );

      const categorizedProducts: CategorizedProducts = {
        food: foodCategory,
        drink: drinkCategory,
        combos: combos || [],
      };

      const initialActiveProductCategory: Category[] =
        activeProductType === ItemTypes.Drink ? drinkCategory : foodCategory;

      const payload: SetCategorizedProducts = {
        categorizedProducts,
        initialActiveProductCategory,
      };
      dispatch(setCategorizedProducts(payload));
      dispatch(setMenuTheme(menuTheme || null));
      return true;
    })
    .catch(() => {
      dispatch(setCategories({ isSuccess: false }));
      return false;
    });
};

export const fetchActiveItemAndProducts = (
  clientId: string,
  dispatch: Dispatch<any>,
  activeProductType: number
) => {
  api
    .get(`/client/${clientId}/items/${activeProductType}`)
    .then((response) => {});
};

export const fetchOrders = (
  clientId: string,
  mtId: string,
  dispatch: Dispatch<any>
) => {
  if (!clientId || !mtId) return false;
  api.get(EndPoints.ORDER.GET_ALL(clientId, mtId)).then((response) => {
    if (response.data.success) {
      dispatch(setCustomerOrders(response.data.orders));
    }
  });
};

export const fetchTableSessionBill = (
  clientId: string,
  tableNumber: string,
  segment: string,
  dispatch: Dispatch<any>,
  tableId?: string
) => {
  if (!clientId || !tableNumber || !segment) return false;
  api
    .get(
      EndPoints.ORDER.TABLE_SESSION_CURRENT(
        clientId,
        tableNumber,
        segment,
        tableId
      )
    )
    .then((response) => {
      if (response.data.success) {
        dispatch(setTableSessionBill(response.data.bill));
      }
    });
};

export const createTableSessionCheckout = async (
  clientId: string,
  sessionId: string,
  successUrl: string,
  cancelUrl: string
) => {
  const response = await api.post(
    EndPoints.PAYMENT.TABLE_SESSION_CHECKOUT(sessionId),
    {
      clientId,
      successUrl,
      cancelUrl,
    }
  );

  return response.data.checkout as {
    checkoutSessionId: string;
    checkoutUrl: string;
    amount: number;
    currency: string;
  };
};

export const startTableSessionPayment = async (
  clientId: string,
  sessionId: string,
  successUrl: string,
  cancelUrl: string
) => {
  const response = await api.post(EndPoints.PAYMENT.TABLE_SESSION_START(sessionId), {
    clientId,
    successUrl,
    cancelUrl,
  });

  return response.data.payment as
    | {
        experience: "hosted_checkout";
        checkoutSessionId: string;
        checkoutUrl: string;
        amount: number;
        currency: string;
      }
    | {
        experience: "embedded_element";
        clientSecret: string;
        amount: number;
        currency: string;
      };
};

export const verifyTableSessionCheckout = async (
  clientId: string,
  sessionId: string,
  checkoutSessionId: string,
  dispatch: Dispatch<any>
) => {
  const response = await api.post(
    EndPoints.PAYMENT.TABLE_SESSION_VERIFY(sessionId),
    {
      clientId,
      checkoutSessionId,
    }
  );

  if (response.data.success && response.data.bill) {
    dispatch(setTableSessionBill(response.data.bill));
  }

  return response.data;
};

export const submitOrder = (order: ServerOrder, dispatch: Dispatch<any>) => {
  if (!order) return;
  dispatch(setOrderSubmitState(OrderSubmitState.Requested));
  api
    .post(EndPoints.ORDER.SUBMIT, order)
    .then((response) => {
      if (response.status === 200) {
        dispatch(setOrderSubmitState(OrderSubmitState.Successful));
        setTimeout(() => {
          // clear cart from state and localstorage
          dispatch(addCart({ cart: [] }));
          set("cart", []);

          dispatch(
            manageModal({ isOpen: false, triggeredBy: MODAL_TRIGGER.CART })
          );
          dispatch(setOrderSubmitState(OrderSubmitState.Stale));
          fetchTableSessionBill(
            order.clientId,
            order.tableNumber,
            order.segment || "",
            dispatch,
            order.tableId
          );
        }, 3000);
      } else {
        dispatch(setOrderSubmitState(OrderSubmitState.Failed));
        setTimeout(() => {
          dispatch(
            manageModal({ isOpen: false, triggeredBy: MODAL_TRIGGER.CART })
          );
          dispatch(setOrderSubmitState(OrderSubmitState.Stale));
        }, 3000);
      }
    })
    .catch(() => {
      dispatch(setOrderSubmitState(OrderSubmitState.Failed));
    });
};

export const logClientActivity = (username: string, ip: string) => {
  const logData = {
    username,
    ip,
    requestTime: new Date().toISOString(),
  };
  api.post(EndPoints.ANALYTICS.CLIENT_ACTIVITY, logData).catch((error) => {
    console.error(error);
  });
};

export const submitWaitlistRequest = (
  waitlistData: WaitlistRequest,
  dispatch: Dispatch<any>
) => {
  dispatch(setWaitlistSubmitState(WaitListSubmitState.Requested));
  api
    .post(EndPoints.WAITLIST.SUBMIT, waitlistData)
    .then((response) => {
      if (response.status === 200) {
        dispatch(setWaitlistSubmitState(WaitListSubmitState.Successful));
      } else {
        dispatch(setWaitlistSubmitState(WaitListSubmitState.Failed));
      }
      setTimeout(() => {
        dispatch(
          manageModal({ isOpen: false, triggeredBy: MODAL_TRIGGER.CART })
        );
        dispatch(setWaitlistSubmitState(WaitListSubmitState.Stale));
      }, 3000);
    })
    .catch(() => {});
};

export const submitRegistrationRequest = async (
  registrationData: RegistrationRequest,
  dispatch: Dispatch<any>
) => {
  try {
    dispatch(setLoadingState(true));
    dispatch(setRegistrationSubmitState(RegistrationSubmitState.Requested));

    const response = await api.post(
      EndPoints.REGISTER.SUBMIT,
      registrationData
    );

    if (response.status === 200) {
      dispatch(setRegistrationSubmitState(RegistrationSubmitState.Successful));
    } else {
      dispatch(setRegistrationSubmitState(RegistrationSubmitState.Failed));
    }

    return response;
  } catch (e) {
    dispatch(setRegistrationSubmitState(RegistrationSubmitState.Failed));
    return e;
  } finally {
    dispatch(setLoadingState(false));
  }
};

export const submitFeedback = async (feedback: Feedback, clientId: string) => {
  if (!feedback) return;
  return await api.post(EndPoints.FEEDBACK.SUBMIT(clientId), {
    ...feedback,
  });
};
